import { useEffect, useState, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Package, Truck, Home as HomeIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatCAD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SHIP_STEPS = ["pending", "processing", "shipped", "delivered"];
const PICKUP_STEPS = ["pending", "processing", "ready"];
const DELIVERY_STEPS = ["pending", "processing", "out_for_delivery", "delivered"];
const STEP_LABELS = {
  pending: "Placed",
  processing: "Preparing",
  out_for_delivery: "On the way",
  delivered: "Delivered",
  shipped: "Shipped",
  ready: "Ready",
};

export default function OrderConfirmation() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [order, setOrder] = useState(location.state?.order || null);
  const [email, setEmail] = useState(location.state?.email || "");
  const [needEmail, setNeedEmail] = useState(false);
  const [error, setError] = useState("");
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const fetchOrder = (em) => {
    api
      .get(`/orders/${id}`, { params: em ? { email: em } : {} })
      .then((r) => { setOrder(r.data); setNeedEmail(false); setError(""); })
      .catch((e) => {
        if (e.response?.status === 403) setNeedEmail(true);
        else setError("Order not found");
      });
  };

  useEffect(() => {
    if (order) return;
    if (user) fetchOrder();
    else if (email) fetchOrder(email);
    else setNeedEmail(true);
    // eslint-disable-next-line
  }, [id, user]);

  // Live status: poll until the order reaches a final state, and celebrate on key milestones.
  const prevStatus = useRef(order?.status);
  useEffect(() => {
    if (order && prevStatus.current && prevStatus.current !== order.status) {
      if (order.status === "ready") {
        toast.success("Your order is ready for pickup! 🎉", {
          description: "Head to 22 Midlake Blvd SE #518, Calgary when you're ready.",
        });
      } else if (order.status === "out_for_delivery") {
        toast.success("Your order is on the way! 🚚", {
          description: "Our driver is heading to you now.",
        });
      } else if (order.status === "delivered") {
        toast.success("Delivered! Enjoy your order. 🎉");
      }
    }
    prevStatus.current = order?.status;
    // Only react to status transitions, not every order refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status]);

  useEffect(() => {
    if (!order || (!user && !email)) return;
    if (["ready", "delivered", "cancelled"].includes(order.status)) return;
    const t = setInterval(() => {
      if (user) fetchOrder();
      else if (email) fetchOrder(email);
    }, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [order?.status, user, email]);

  if (needEmail && !order)
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <h1 className="font-display text-4xl font-semibold text-forest">View your order</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the email you used at checkout.</p>
        <input data-testid="view-order-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="mt-5 w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-clay" />
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        <button data-testid="view-order-submit" onClick={() => fetchOrder(email)} className="mt-4 w-full rounded-full bg-clay py-3 text-sm font-semibold text-white hover:bg-[#A3161F]">View order</button>
      </div>
    );

  if (!order)
    return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-muted-foreground">Loading order…</div>;

  const isPickup = order.shipping_method?.id === "pickup";
  const isDelivery = ["standard", "express"].includes(order.shipping_method?.id);
  const STATUS_STEPS = isPickup ? PICKUP_STEPS : isDelivery ? DELIVERY_STEPS : SHIP_STEPS;
  const activeIdx = STATUS_STEPS.indexOf(order.status);

  return (
    <div data-testid="order-confirmation-page" className="mx-auto max-w-4xl px-5 py-12 lg:px-10">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 18 }} className="flex flex-col items-center text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-leaf/15 text-forest">
          <CheckCircle2 className="h-11 w-11" />
        </div>
        <h1 className="mt-6 font-display text-5xl font-semibold tracking-tight text-forest lg:text-6xl">Thank you!</h1>
        <p className="mt-3 text-muted-foreground">Your order <span className="font-semibold text-forest" data-testid="order-number">{order.order_number}</span> is confirmed.</p>
        {order.payment?.mocked && (
          <p className="mt-2 rounded-full bg-ochre/15 px-4 py-1 text-xs font-medium text-forest">Demo payment — no real charge was made</p>
        )}
      </motion.div>

      {/* Tracker */}
      <div className="mt-12 rounded-[1.5rem] border border-border/70 bg-white p-6 lg:p-8">
        <h2 className="font-display text-2xl font-semibold text-forest">Order status</h2>
        <span data-testid="order-status-value" className="sr-only">{order.status}</span>
        {isDelivery && order.status === "out_for_delivery" && order.eta_arrival && (() => {
          const remaining = Math.round((new Date(order.eta_arrival).getTime() - nowTs) / 60000);
          return (
            <div data-testid="eta-countdown" className="mt-4 flex items-center gap-3 rounded-xl border border-clay/30 bg-clay/5 px-4 py-3">
              <Truck className="h-5 w-5 shrink-0 text-clay" />
              <p className="text-sm font-medium text-forest">
                {remaining > 1 ? `Your driver is on the way — arriving in ~${remaining} min` : "Your driver is arriving any moment now!"}
              </p>
            </div>
          );
        })()}
        <div className="mt-8 flex items-center">
          {STATUS_STEPS.map((s, i) => {
            const shipIcons = [Package, Package, Truck, HomeIcon];
            const pickupIcons = [Package, Package, MapPin];
            const deliveryIcons = [Package, Package, Truck, HomeIcon];
            const Icon = (isPickup ? pickupIcons : isDelivery ? deliveryIcons : shipIcons)[i];
            const done = i <= activeIdx;
            return (
              <div key={s} data-testid={`status-step-${s}`} data-active={done} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-center">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors ${done ? "border-forest bg-forest text-white" : "border-border bg-white text-muted-foreground"}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className={`mt-2 text-center text-xs font-medium ${done ? "text-forest" : "text-muted-foreground"}`}>{STEP_LABELS[s] || s}</span>
                </div>
                {i < STATUS_STEPS.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < activeIdx ? "bg-forest" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 grid gap-8 md:grid-cols-2">
        {/* Items */}
        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <h2 className="font-display text-2xl font-semibold text-forest">Items</h2>
          <div className="mt-4 space-y-3">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3">
                <img src={i.image_url} alt={i.name} className="h-14 w-14 rounded-lg object-cover" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-forest">{i.name}</p>
                  <p className="text-xs text-muted-foreground">Qty {i.quantity} · {i.unit}</p>
                </div>
                <span className="text-sm font-semibold text-forest">{formatCAD(i.line_total_cents)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="text-forest">{formatCAD(order.subtotal_cents)}</span></div>
            {order.discount_cents > 0 && (
              <div className="flex justify-between text-clay"><span>Discount{order.discount?.code ? ` (${order.discount.code})` : ""}</span><span>−{formatCAD(order.discount_cents)}</span></div>
            )}
            <div className="flex justify-between text-muted-foreground"><span>Shipping</span><span className="text-forest">{order.shipping_cents === 0 ? "Free" : formatCAD(order.shipping_cents)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Tax</span><span className="text-forest">{formatCAD(order.tax_cents)}</span></div>
            <div className="flex justify-between border-t border-border pt-2 font-bold text-forest"><span>Total</span><span>{formatCAD(order.total_cents)}</span></div>
          </div>
          {order.gst_number && (
            <p data-testid="gst-number" className="mt-3 text-right text-xs text-muted-foreground">GST/HST No. {order.gst_number}</p>
          )}
        </div>

        {/* Delivery / pickup details */}
        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <h2 className="font-display text-2xl font-semibold text-forest">{isPickup ? "Pickup" : "Delivery"}</h2>
          <div className="mt-4 flex items-start gap-3 text-sm">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
            {isPickup ? (
              <div className="text-muted-foreground">
                <p className="font-medium text-forest">Bambam African Mart</p>
                <p>22 Midlake Blvd SE #518</p>
                <p>Calgary, AB, Canada</p>
              </div>
            ) : (
              <div className="text-muted-foreground">
                <p className="font-medium text-forest">{order.customer_name}</p>
                <p>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</p>
                <p>{order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
                <p>{order.shipping_address.country}</p>
              </div>
            )}
          </div>
          <div className="mt-5 flex items-start gap-3 text-sm">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
            <div className="text-muted-foreground">
              <p className="font-medium text-forest">{order.shipping_method.name}</p>
              <p>{order.shipping_method.carrier} · {order.shipping_method.eta}</p>
              {isPickup && order.pickup_slot && (
                <p data-testid="pickup-slot" className="mt-1 font-medium text-forest">Pickup window: {order.pickup_slot}</p>
              )}
            </div>
          </div>
          {isPickup && (
            <div data-testid="pickup-confirm-note" className="mt-4 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-forest">
              We'll email you at <span className="font-medium">{order.customer_email}</span>{order.customer_phone ? " and text your phone" : ""} as soon as your order is ready to collect in store.
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap justify-center gap-4">
        <Link to="/shop" className="rounded-full bg-clay px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A3161F]">Continue shopping</Link>
        {user && <Link to="/account" className="rounded-full border border-border px-8 py-3.5 text-sm font-semibold text-forest hover:bg-forest/5">View my orders</Link>}
      </div>
    </div>
  );
}
